import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer, Edit, Save, X } from "lucide-react";

export default function POPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  // "Edit Notes & Terms" modal — user can tune the free-form Remarks (bottom-left)
  // and the printable Terms & Conditions block without regenerating the PO.
  const [editing, setEditing] = useState(false);
  const [draftNotes, setDraftNotes] = useState("");
  const [draftTerms, setDraftTerms] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/purchase-orders/${id}`).then((r) => setPo(r.data));
  }, [id]);

  const openEdit = () => {
    setDraftNotes(po?.notes || "");
    setDraftTerms(po?.terms_and_conditions || "");
    setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/purchase-orders/${id}`, {
        notes: draftNotes,
        terms_and_conditions: draftTerms,
      });
      setPo(data);
      setEditing(false);
      toast.success("PO notes & terms updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!po) return <div className="p-8 text-sm text-slate-500">Loading PO…</div>;

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      {/* Toolbar */}
      <div className="no-print max-w-[850px] mx-auto px-4 mb-4 flex justify-between">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex gap-2">
          <button
            onClick={openEdit}
            data-testid="po-edit-notes-terms-btn"
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"
          >
            <Edit size={14} /> Edit Notes & Terms
          </button>
          <button
            onClick={() => window.print()}
            data-testid="print-now-btn"
            className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"
          >
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      {editing && (
        <div className="no-print fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !saving && setEditing(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">Edit PO Notes & Terms — {po.po_number}</div>
              <button onClick={() => !saving && setEditing(false)} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                  Notes / Remarks <span className="text-slate-400 font-normal normal-case">— printed at the bottom-left of the PO</span>
                </label>
                <textarea
                  data-testid="po-edit-notes"
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  rows={4}
                  placeholder="Any special instructions or context…"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/30"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5">
                  Terms & Conditions <span className="text-slate-400 font-normal normal-case">— printed above the Authorised Signatory block</span>
                </label>
                <textarea
                  data-testid="po-edit-terms"
                  value={draftTerms}
                  onChange={(e) => setDraftTerms(e.target.value)}
                  rows={10}
                  placeholder={"1. Delivery: as per PO date.\n2. Payment: as per agreed terms.\n3. Quality: goods subject to inspection on receipt.\n4. Jurisdiction: …"}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#1D2951]/30"
                />
                <div className="text-[11px] text-slate-500 mt-1">One item per line. Use plain text — this block prints as-is on the PO.</div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => !saving && setEditing(false)} disabled={saving} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white hover:bg-slate-50 disabled:opacity-50">Cancel</button>
              <button onClick={saveEdit} disabled={saving} data-testid="po-edit-save-btn" className="px-4 py-1.5 text-sm font-semibold text-white rounded-md bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-50 inline-flex items-center gap-1.5">
                <Save size={13} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page */}
      <div
        className="print-page bg-white max-w-[850px] mx-auto p-10 shadow-sm border border-slate-200 text-slate-900"
        data-testid="po-print-page"
      >
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-slate-900 pb-4">
          <div className="max-w-[60%]">
            <div className="text-xl font-bold tracking-tight">{po.company.name}</div>
            <div className="text-xs text-slate-700 mt-1 whitespace-pre-line">
              {po.company.address}
            </div>
            <div className="text-xs text-slate-700 mt-1">
              <span className="font-semibold">GST:</span> {po.company.gst} ·{" "}
              <span className="font-semibold">Phone:</span> {po.company.contact}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold tracking-wider uppercase">
              Purchase Order
            </div>
            <div className="text-xs mt-2 font-mono">
              <div>
                <span className="text-slate-500">Order No:</span>{" "}
                <span className="font-semibold">{po.po_number}</span>
              </div>
              <div>
                <span className="text-slate-500">Date:</span> {po.date}
              </div>
            </div>
          </div>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 mt-4 text-xs">
          <div className="space-y-1 pr-4">
            <Row k="Payment Terms" v={po.payment_terms} />
            <Row k="Terms" v={po.terms} />
            <Row k="Taxes" v={po.taxes} />
          </div>
          <div className="space-y-1 pl-4 border-l border-slate-200">
            <Row k="Delivery Date" v={po.delivery_date || "—"} />
            <Row k="Validity" v={po.validity} />
          </div>
        </div>

        {/* Supplier */}
        <div className="mt-6 border border-slate-300 rounded">
          <div className="bg-slate-100 px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold border-b border-slate-300">
            Supplier
          </div>
          <div className="p-3 text-xs">
            <div className="font-semibold text-sm">{po.supplier.name}</div>
            <div className="text-slate-700">{po.supplier.address}</div>
            <div className="text-slate-700">
              <span className="font-semibold">GST:</span> {po.supplier.gst || "—"}
            </div>
          </div>
        </div>

        <p className="mt-5 text-sm italic text-slate-700">
          We are pleased to place the order for the following items
        </p>

        {/* Items table */}
        <table className="w-full mt-3 text-xs border border-slate-900">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="px-2 py-1.5 border border-slate-900 w-10 text-center">S.N.</th>
              <th className="px-2 py-1.5 border border-slate-900 text-left">Description of Goods</th>
              <th className="px-2 py-1.5 border border-slate-900 text-center">HSN/SAC</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Qty</th>
              <th className="px-2 py-1.5 border border-slate-900 text-center">Unit</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Price</th>
              <th className="px-2 py-1.5 border border-slate-900 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {po.items.map((it) => (
              <tr key={it.sn}>
                <td className="px-2 py-1.5 border border-slate-300 text-center font-mono">{it.sn}</td>
                <td className="px-2 py-1.5 border border-slate-300">
                  {it.description}
                  {(it.grade || it.rm_type) && (
                    <div className="text-[10px] text-slate-600 mt-0.5">
                      {it.grade && <>Grade: <span className="font-semibold">{it.grade}</span></>}
                      {it.grade && it.rm_type && " · "}
                      {it.rm_type && <>Type: <span className="font-semibold">{it.rm_type}</span></>}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1.5 border border-slate-300 text-center font-mono">{it.hsn_sac || "—"}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{it.quantity}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-center">{it.unit}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{Number(it.price).toFixed(2)}</td>
                <td className="px-2 py-1.5 border border-slate-300 text-right font-mono">{Number(it.amount).toFixed(2)}</td>
              </tr>
            ))}
            {Array.from({ length: Math.max(0, 5 - po.items.length) }).map((_, i) => (
              <tr key={`pad-${i}`}>
                {Array.from({ length: 7 }).map((__, j) => (
                  <td key={j} className="px-2 py-1.5 border border-slate-300">&nbsp;</td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold">
              <td colSpan="6" className="px-2 py-1.5 border border-slate-900 text-right">Subtotal</td>
              <td className="px-2 py-1.5 border border-slate-900 text-right font-mono">{po.subtotal.toFixed(2)}</td>
            </tr>
            <tr>
              <td colSpan="6" className="px-2 py-1.5 border border-slate-900 text-right">Add IGST @ {po.igst_rate}%</td>
              <td className="px-2 py-1.5 border border-slate-900 text-right font-mono">{po.igst_amount.toFixed(2)}</td>
            </tr>
            <tr className="font-bold bg-slate-100">
              <td colSpan="6" className="px-2 py-1.5 border border-slate-900 text-right uppercase tracking-wider">Grand Total (₹)</td>
              <td className="px-2 py-1.5 border border-slate-900 text-right font-mono">{po.grand_total.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>

        {/* Tax Breakdown */}
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold mb-1">Tax Breakdown</div>
          <table className="text-xs border border-slate-900">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-3 py-1.5 border border-slate-900">Tax Rate</th>
                <th className="px-3 py-1.5 border border-slate-900">Taxable Amount</th>
                <th className="px-3 py-1.5 border border-slate-900">IGST Amount</th>
                <th className="px-3 py-1.5 border border-slate-900">Total Tax</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-3 py-1.5 border border-slate-300 text-center font-mono">{po.igst_rate}%</td>
                <td className="px-3 py-1.5 border border-slate-300 text-right font-mono">{po.subtotal.toFixed(2)}</td>
                <td className="px-3 py-1.5 border border-slate-300 text-right font-mono">{po.igst_amount.toFixed(2)}</td>
                <td className="px-3 py-1.5 border border-slate-300 text-right font-mono">{po.igst_amount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Amount in words */}
        <div className="mt-4 border border-slate-300 px-3 py-2 text-xs">
          <span className="font-semibold uppercase tracking-wider mr-2">Amount in Words:</span>
          <span className="italic">{po.amount_in_words}</span>
        </div>

        {/* Terms & Conditions — editable, prints as-is */}
        {(po.terms_and_conditions || "").trim() && (
          <div className="mt-4 border border-slate-300 px-3 py-2 text-xs" data-testid="po-print-terms">
            <div className="font-semibold uppercase tracking-wider mb-1">Terms & Conditions</div>
            <div className="text-slate-700 whitespace-pre-line leading-relaxed">{po.terms_and_conditions}</div>
          </div>
        )}

        {/* Footer — Notes on bottom-left, Signatory on bottom-right */}
        <div className="mt-12 flex items-end justify-between gap-8">
          <div className="max-w-[55%] text-xs" data-testid="po-print-notes">
            {(po.notes || "").trim() && (
              <>
                <div className="font-semibold uppercase tracking-wider mb-1">Notes / Remarks</div>
                <div className="text-slate-700 whitespace-pre-line">{po.notes}</div>
              </>
            )}
          </div>
          <div className="text-right">
            <div className="border-t border-slate-900 pt-1 mt-12 px-8 text-xs">
              <div className="font-semibold">Authorised Signatory</div>
              <div className="text-slate-700 mt-0.5">{po.company.name}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex">
    <div className="w-32 text-slate-500">{k}:</div>
    <div className="flex-1 font-medium">{v || "—"}</div>
  </div>
);
